import { X, MessageCircle } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import env from 'env';
import { useCancellationPlan } from 'src/context/billing';

import CallToActionBanner from './CallToActionBanner';

const DowngradeCard = ({ title, text }: { title: string; text: string }) => {
  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-6">
      <div className="space-y-1.5 relative">
        <X className="size-6 text-destructive absolute left-0 top-0" />
        <div className="text-base text-foreground pl-8">{title}</div>
        <div className="text-sm text-muted-foreground">{text}</div>
      </div>
    </div>
  );
};

const items = [
  {
    title: 'Application continuity solutions',
    text: 'Your Codekeeper account includes several layers of application continuity protection to help you avoid vendor dependency and information loss.',
  },
  {
    title: 'Free software resilience validation',
    text: 'All Codekeeper plans include our Software Backup solution as a feature, equipping you with the ability to validate your software resilience.',
  },
  {
    title: 'Compliance alignment support',
    text: 'Implementing our solutions can be used as evidence for your ISMS, and help you avoid legal and regulatory challenges associated with your operations.',
  },
  {
    title: 'Concierge onboarding support',
    text: 'Our customer service team is here to provide support for any of the application continuity solutions available on your dashboard.... and more.',
  },
];

const DowngradeConfirmation = () => {
  const {
    ui: { setStep },
  } = useCancellationPlan();

  const onCancel = () => setStep('plan-downgrade-in-progress');

  const onExploreSolutions = () => window.open('https://codekeeper.co/solutions', '_blank');

  const onContactUs = () => window.open(`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`, '_blank');

  const onKeepPlan = () => setStep('keep-plan');

  return (
    <Fragment>
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          That means <strong className="font-bold">no more:</strong>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          {items.map((item, index) => (
            <DowngradeCard key={index} {...item} />
          ))}
        </div>
      </div>
      <h6 className="text-base text-foreground font-bold">Here’s what you can do instead:</h6>
      <div className="space-y-4">
        <CallToActionBanner
          title="Change your plan"
          description="Not a good fit? Explore our other solutions to find something that fits your needs and budget better."
          action={
            <Button variant="secondary" onClick={onExploreSolutions}>
              Explore solutions
            </Button>
          }
          variant="secondary"
        />
        <CallToActionBanner
          title="Get a better offer"
          description="Found an alternative provider with a better rate? Send over your invoice or quote - we pride ourselves in being the best priced solution on the market. "
          action={
            <Button variant="secondary" onClick={onContactUs}>
              Send us a message
            </Button>
          }
          variant="secondary"
        />
        <CallToActionBanner
          title="Not sure you want to cancel?"
          description="Don’t. Reach out to us, and we’ll get in touch about how we can help you make the most of your Codekeeper account."
          action={
            <Button variant="secondary" onClick={onContactUs}>
              <MessageCircle /> Contact us
            </Button>
          }
        />
      </div>
      <div className="flex justify-end gap-2.5">
        <Button variant="secondary" onClick={onCancel}>
          Cancel plan
        </Button>
        <Button onClick={onKeepPlan}>Keep plan</Button>
      </div>
    </Fragment>
  );
};

export default DowngradeConfirmation;
